import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"

export const Route = createFileRoute('/integrations/quickbooks/callback')({
    component: QuickBooksCallbackPage,
})

interface QuickBooksCallbackResponse {
    success: boolean
    message: string
    realmId?: string
}

function QuickBooksCallbackPage() {
    const navigate = useNavigate()
    const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing')
    const [message, setMessage] = useState('Processing QuickBooks connection...')

    useEffect(() => {
        const handleCallback = async () => {
            try {
                // Get URL parameters
                const urlParams = new URLSearchParams(window.location.search)
                const code = urlParams.get('code')
                const realmId = urlParams.get('realmId')
                const state = urlParams.get('state')
                const error = urlParams.get('error')
                const errorDescription = urlParams.get('error_description')

                // Check for OAuth errors
                if (error) {
                    setStatus('error')
                    setMessage(`QuickBooks authorization failed: ${errorDescription || error}`)
                    return
                }

                // Check for required parameters
                if (!code || !realmId || !state) {
                    setStatus('error')
                    setMessage('Missing required parameters from QuickBooks response.')
                    return
                }

                // Send the callback data to our API
                const response = await fetch(`${import.meta.env.VITE_API_URL}/integrations/quickbooks/callback`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}` // Adjust based on your auth setup
                    },
                    body: JSON.stringify({
                        code,
                        realmId,
                        state
                    })
                })

                if (response.ok) {
                    const result: QuickBooksCallbackResponse = await response.json()
                    if (result.success) {
                        setStatus('success')
                        setMessage('QuickBooks connected successfully!')

                        // Redirect to integrations page after 2 seconds
                        setTimeout(() => {
                            window.location.href = '/_app/integrations/quickbooks'
                        }, 2000)
                    } else {
                        setStatus('error')
                        setMessage(result.message || 'Failed to connect QuickBooks')
                    }
                } else {
                    setStatus('error')
                    setMessage('Failed to process QuickBooks connection')
                }
            } catch (error) {
                console.error('Error processing callback:', error)
                setStatus('error')
                setMessage('An error occurred while processing the QuickBooks connection')
            }
        }

        handleCallback()
    }, [navigate])

    const handleReturnToSettings = () => {
        window.location.href = '/_app/integrations/quickbooks'
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <CardTitle className="flex items-center justify-center gap-2">
                        {status === 'processing' && <Loader2 className="h-6 w-6 animate-spin" />}
                        {status === 'success' && <CheckCircle className="h-6 w-6 text-green-600" />}
                        {status === 'error' && <AlertCircle className="h-6 w-6 text-red-600" />}
                        QuickBooks Integration
                    </CardTitle>
                    <CardDescription>
                        {status === 'processing' && 'Setting up your QuickBooks connection...'}
                        {status === 'success' && 'Connection established successfully'}
                        {status === 'error' && 'Connection failed'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Alert className={
                        status === 'success' ? "border-green-200 bg-green-50" :
                            status === 'error' ? "border-red-200 bg-red-50" :
                                "border-blue-200 bg-blue-50"
                    }>
                        <AlertDescription className={
                            status === 'success' ? "text-green-700" :
                                status === 'error' ? "text-red-700" :
                                    "text-blue-700"
                        }>
                            {message}
                        </AlertDescription>
                    </Alert>

                    {status !== 'processing' && (
                        <Button
                            onClick={handleReturnToSettings}
                            className="w-full"
                            variant={status === 'success' ? 'default' : 'outline'}
                        >
                            {status === 'success' ? 'Continue to Settings' : 'Back to Settings'}
                        </Button>
                    )}

                    {status === 'success' && (
                        <p className="text-sm text-gray-600 text-center">
                            Redirecting to settings in a few seconds...
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
